import { GamesData, Game, SportType, FilterOptions, SoccerCompetition, GamesDict, SportLimits, SportDefaultValues } from '@/types';
import { toast } from 'sonner';
import { resolveGameTeamColors } from '@/utils/teamColors';

export const CANONICAL_GAMES_API_BASE_URL = 'https://api.skore.info';

function normalizeApiBaseUrl(value: unknown): string {
  const baseUrl = typeof value === 'string' && value.trim() ? value.trim() : CANONICAL_GAMES_API_BASE_URL;
  return baseUrl.replace(/\/+$/, '');
}

export const GAMES_API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_URL);
export const GAMES_DATA_URL = `${GAMES_API_BASE_URL}/api/games.json`;
export const UPCOMING_GAMES_DATA_URL = `${GAMES_API_BASE_URL}/api/upcoming-games.json`;

// Define sport-specific limits
export const sportLimits: Record<SportType, SportLimits> = {
  'nba': { min: 0, max: 50 },
  'soccer': { min: 0, max: 10 },
  'nfl': { min: 0, max: 50 },
  'mlb': { min: 0, max: 30 },
  'nhl': { min: 0, max: 20 }
};

// Define sport-specific default values
export const sportDefaultValues: Record<SportType, SportDefaultValues> = {
  'nba': { default: 10 },    // Default points difference for NBA
  'soccer': { default: 1 },  // Default total goals for soccer
  'nfl': { default: 14 },    // Default points difference for NFL
  'mlb': { default: 6 },     // Default run difference for MLB
  'nhl': { default: 1 }      // Default total goals for NHL
};


// Add a cleanup function to clear cached data
export function clearGamesCache() {
  // Clear any cached data
  if (typeof window !== 'undefined') {
    // Clear session storage if used
    sessionStorage.removeItem('gamesData');
  }
}

async function fetchGamesDataFromUrl(url: string): Promise<GamesData | null> {
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch games: ${response.status}`);
    }
    
    const data: GamesData = await response.json();
    
    // Process the data to minimize memory usage
    const processedData = {
      ...data,
      games: Object.fromEntries(
        Object.entries(data.games).map(([sportType, games]) => [
          sportType,
          Object.fromEntries(
            Object.entries(games).map(([gameId, gameData]) => {
              const sportTypeValue = sportType as SportType;
              const gameWithResolvedColors = resolveGameTeamColors({
                ...gameData,
                id: gameId,
                sportType: sportTypeValue
              });

              return [
                gameId,
                {
                  ...gameWithResolvedColors,
                  // Only keep essential fields (and ensure excitementScore is included)
                  id: gameId,
                  sportType: sportTypeValue,
                  homeScore: gameData.homeScore,
                  awayScore: gameData.awayScore,
                  status: gameData.status,
                  competition: gameData.competition,
                  stage: gameData.stage,
                  excitementScore: gameData.excitementScore,
                  // Add computed fields
                  pointsDifference: gameData.homeScore !== undefined && gameData.awayScore !== undefined
                    ? Math.abs(gameData.homeScore - gameData.awayScore)
                    : undefined,
                  totalScore: gameData.homeScore !== undefined && gameData.awayScore !== undefined
                    ? gameData.homeScore + gameData.awayScore
                    : undefined
                }
              ];
            })
          )
        ])
      )
    };
    
    return processedData;
  } catch (error) {
    console.error('Error fetching games data:', error);
    const isStoreCapture = typeof window !== 'undefined' && window.sessionStorage.getItem('skore.capture-mode') === 'true';
    if (!isStoreCapture) toast.error('Failed to load games data. Please try again later.', {
      style: { 
        background: '#ff3333',
        color: '#ffffff', 
        fontWeight: 'bold',
        fontSize: '16px',
        border: '2px solid #ff0000',
        padding: '16px',
        boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)'
      },
      duration: 5000,
      icon: '⚠️'
    });
    return null;
  }
}

export async function fetchGamesData(): Promise<GamesData | null> {
  return fetchGamesDataFromUrl(GAMES_DATA_URL);
}

export async function fetchUpcomingGamesData(): Promise<GamesData | null> {
  return fetchGamesDataFromUrl(UPCOMING_GAMES_DATA_URL);
}

const POINT_DIFFERENCE_SPORTS: SportType[] = ['nba', 'nfl', 'mlb'];
const TOTAL_SCORE_SPORTS: SportType[] = ['soccer', 'nhl'];

function shouldTreatMaxValueAsAll(filters: FilterOptions): boolean {
  if (filters.maxValue === undefined) {
    return false;
  }

  return POINT_DIFFERENCE_SPORTS.includes(filters.sportType) && filters.maxValue >= sportLimits[filters.sportType].max;
}

export function filterGames(gamesDict: GamesDict, filters: FilterOptions): Game[] {
  // Convert nested structure to a flat array format
  const flatGames: Game[] = [];
  
  Object.entries(gamesDict).forEach(([sportType, games]) => {
    Object.entries(games).forEach(([gameId, gameData]) => {
      const game = resolveGameTeamColors({
        id: gameId,
        sportType: sportType as SportType,
        ...gameData
      });
      
      // Add computed fields based on sport type
      if (game.homeScore !== undefined && game.awayScore !== undefined) {
        if (game.sportType === 'nba' || game.sportType === 'nfl' || game.sportType === 'mlb') {
          game.pointsDifference = Math.abs(game.homeScore - game.awayScore);
        } else if (game.sportType === 'soccer' || game.sportType === 'nhl') {
          game.totalScore = game.homeScore + game.awayScore;
        }
      }
      
      flatGames.push(game);
    });
  });
  
  // Filter by sport type
  let filtered = flatGames.filter(game => game.sportType === filters.sportType);
  
  // Filter soccer games by competitions if specified
  if (filters.sportType === 'soccer' && filters.competitions && filters.competitions.length > 0) {
    filtered = filtered.filter(game => 
      game.competition && filters.competitions?.includes(game.competition)
    );
  }
  
  // Filter by different criteria based on sport type
  if (filters.maxValue !== undefined) {
    const treatMaxValueAsAll = shouldTreatMaxValueAsAll(filters);

    filtered = filtered.filter(game => {
      if (POINT_DIFFERENCE_SPORTS.includes(game.sportType)) {
        if (treatMaxValueAsAll) {
          return game.status.type === 'completed';
        }

        // For NBA/NFL/MLB, filter based on points difference
        return game.status.type === 'completed' && 
               game.pointsDifference !== undefined && 
               game.pointsDifference <= filters.maxValue;
      } else if (TOTAL_SCORE_SPORTS.includes(game.sportType)) {
        // For soccer and NHL, filter based on total score
        return game.status.type === 'completed' && 
               game.totalScore !== undefined && 
               game.totalScore >= filters.maxValue;
      }
      
      // Default behavior for other sports
      return true;
    });
  }
  
  // Apply min value filter if provided (only for sports with min value metrics)
  if (filters.minValue !== undefined) {
    filtered = filtered.filter(game => {
      if (filters.sportType === 'soccer' || filters.sportType === 'nhl') {
        // For soccer and NHL, apply additional minimum filter
        return game.status.type === 'completed' && 
               game.totalScore !== undefined && 
               game.totalScore >= filters.minValue!;
      }
      return true;
    });
  }
  
  return filtered;
}

// Interpret filter based on sport type
export function interpretFilter(sportType: SportType, value: number): string {
  if (POINT_DIFFERENCE_SPORTS.includes(sportType)) {
    if (value >= sportLimits[sportType].max) {
      return 'All completed games';
    }

    return `Games with point difference less than ${value}`;
  } else if (TOTAL_SCORE_SPORTS.includes(sportType)) {
    return `Games with total goals ${value} or more`;
  }
  
  return '';
}
